import type {
  ContentBrief, ContentBriefDraft, ContentBriefPublication,
  ContentConnectorType, ContentDispatchStatus,
  ContentPublishingDispatch,
} from "../types";
import { isEmailDispatch } from "./email";

export const isPublicationStaleForDispatch = (
  brief: Pick<ContentBrief, "updated_at">,
  draft: Pick<ContentBriefDraft, "updated_at"> | null,
  publication: Pick<ContentBriefPublication, "draft_updated_at_snapshot" | "piece_updated_at_snapshot">,
): boolean => {
  if (publication.piece_updated_at_snapshot && new Date(brief.updated_at).getTime() > new Date(publication.piece_updated_at_snapshot).getTime()) {
    return true;
  }
  if (draft && publication.draft_updated_at_snapshot && new Date(draft.updated_at).getTime() > new Date(publication.draft_updated_at_snapshot).getTime()) {
    return true;
  }
  return false;
};

export const getDispatchesForPublication = (
  publicationId: string,
  dispatches: ContentPublishingDispatch[],
): ContentPublishingDispatch[] =>
  dispatches
    .filter((dispatch) => dispatch.publication_id === publicationId)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

export const getDispatchesForConnector = (
  connectorId: string,
  dispatches: ContentPublishingDispatch[],
): ContentPublishingDispatch[] =>
  dispatches
    .filter((dispatch) => dispatch.connector_id === connectorId)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

export type ConnectorHealth = {
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  lastErrorMessage: string | null;
  successRate: number | null; // 0..1 over last 20
  total: number;
  consecutiveFailures: number;
  needsAttention: boolean;
};

export const getConnectorHealth = (
  connectorId: string,
  dispatches: ContentPublishingDispatch[],
): ConnectorHealth => {
  const rows = getDispatchesForConnector(connectorId, dispatches);
  const window = rows.slice(0, 20);
  const succeeded = window.filter((d) => d.status === "succeeded").length;
  const failed = window.filter((d) => d.status === "failed").length;
  const total = succeeded + failed;
  const lastSuccess = rows.find((d) => d.status === "succeeded") ?? null;
  const lastFailure = rows.find((d) => d.status === "failed") ?? null;
  let consecutiveFailures = 0;
  for (const d of rows) {
    if (d.status === "failed") consecutiveFailures += 1;
    else if (d.status === "succeeded") break;
  }
  return {
    lastSuccessAt: lastSuccess?.completed_at ?? lastSuccess?.created_at ?? null,
    lastFailureAt: lastFailure?.completed_at ?? lastFailure?.created_at ?? null,
    lastErrorMessage: lastFailure?.error_message ?? null,
    successRate: total > 0 ? succeeded / total : null,
    total,
    consecutiveFailures,
    needsAttention: consecutiveFailures >= 5,
  };
};

export type DispatchActivityFilters = {
  status?: "all" | ContentDispatchStatus;
  connectorId?: string | "all";
  rangeDays?: 1 | 7 | 30 | "all";
  type?: "all" | ContentConnectorType;
};

export const filterDispatches = (
  dispatches: ContentPublishingDispatch[],
  filters: DispatchActivityFilters,
): ContentPublishingDispatch[] => {
  const now = Date.now();
  const horizon =
    filters.rangeDays && filters.rangeDays !== "all"
      ? now - filters.rangeDays * 24 * 60 * 60 * 1000
      : null;
  return dispatches
    .filter((d) => (filters.status && filters.status !== "all" ? d.status === filters.status : true))
    .filter((d) => (filters.connectorId && filters.connectorId !== "all" ? d.connector_id === filters.connectorId : true))
    .filter((d) => (horizon ? new Date(d.created_at).getTime() >= horizon : true))
    .filter((d) => {
      if (!filters.type || filters.type === "all") return true;
      const isEmail = isEmailDispatch(d);
      return filters.type === "email" ? isEmail : !isEmail;
    })
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
};

export type DispatchMonthlySummary = {
  total: number;
  succeeded: number;
  failed: number;
  cancelled: number;
  successRate: number | null;
  lastFailureAt: string | null;
};

export const getDispatchMonthlySummary = (
  dispatches: ContentPublishingDispatch[],
  reference: Date = new Date(),
): DispatchMonthlySummary => {
  const start = new Date(reference.getFullYear(), reference.getMonth(), 1).getTime();
  const window = dispatches.filter((d) => new Date(d.created_at).getTime() >= start);
  const succeeded = window.filter((d) => d.status === "succeeded").length;
  const cancelled = window.filter(
    (d) => d.status === "failed" && (d.error_message ?? "").toLowerCase().includes("cancelled"),
  ).length;
  const failed = window.filter((d) => d.status === "failed").length - cancelled;
  const total = succeeded + failed + cancelled;
  const lastFailure = window
    .filter((d) => d.status === "failed")
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
  return {
    total,
    succeeded,
    failed,
    cancelled,
    successRate: total > 0 ? succeeded / total : null,
    lastFailureAt: lastFailure?.created_at ?? null,
  };
};
